// All hooks call the real API.

import {
  keepPreviousData,
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { useAuth } from '@/context/AuthContext';
import { useTranslation } from 'react-i18next';
import { TranslationNS } from '@/constants/translation-namespaces';
import toast from 'react-hot-toast';
import { getPresignedUrl, uploadFileToS3 } from '@/api/file';
import type {
  CreateTaskListPayload,
  UpdateTaskListPayload,
  TaskListType,
  GetTaskListsParams,
  GetTaskListsResponse,
  CreateTaskApiPayload,
  TaskEntity,
} from '@/types/task-lists';
import {
  getTaskLists,
  getTaskListById,
  getTask,
  createTaskList,
  updateTaskList,
  deleteTaskList,
  createTask,
  updateTask,
  deleteTask,
  reorderTask,
  getParentTasks,
} from '@/api/task-lists';
import { QueryKeys } from '@gokrew/models/constants';
import { fetchAllPages } from '@gokrew/ui/utility/fetchAllPages';

export function useGetTaskLists(
  type: TaskListType,
  params: GetTaskListsParams = {},
  options: { enabled?: boolean } = {}
) {
  const { companyId } = useAuth();

  return useQuery({
    queryKey: [QueryKeys.TaskListsList, companyId, type, params],
    queryFn: () => getTaskLists(companyId!, type, params),
    placeholderData: keepPreviousData,
    enabled: !!companyId && (options.enabled ?? true),
  });
}

/**
 * Every task list template of a type matching `params` (all pages; the API
 * answers 10 by default), for pickers that offer them whole.
 */
export function useAllTaskLists(
  type: TaskListType,
  params: Omit<GetTaskListsParams, 'page' | 'limit'> = {},
  options: { enabled?: boolean } = {}
) {
  const { companyId } = useAuth();

  return useQuery({
    queryKey: [QueryKeys.TaskListsList, companyId, type, 'all', params],
    queryFn: () =>
      fetchAllPages((page, limit) =>
        getTaskLists(companyId!, type, { ...params, page, limit })
      ),
    enabled: !!companyId && (options.enabled ?? true),
  });
}

export function useGetTaskListById(
  id: string | undefined,
  type: TaskListType = 'onboarding'
) {
  const { companyId } = useAuth();

  const query = useQuery({
    queryKey: [QueryKeys.TaskListsDetail, companyId, id],
    queryFn: () => getTaskListById(companyId!, type, id!),
    enabled: !!companyId && !!id,
  });

  return {
    ...query,
    data: query.data?.data ?? null,
  };
}

export function useCreateTaskList() {
  const { companyId } = useAuth();
  const { t } = useTranslation(TranslationNS.TaskLists);
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateTaskListPayload) =>
      createTaskList(companyId!, payload),
    onSuccess: () => {
      toast.success(t('toast.task_list_created'));
      qc.invalidateQueries({ queryKey: [QueryKeys.TaskListsList, companyId] });
    },
  });
}

export function useUpdateTaskList() {
  const { companyId } = useAuth();
  const { t } = useTranslation(TranslationNS.TaskLists);
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      type,
      payload,
    }: {
      id: string;
      type: TaskListType;
      payload: UpdateTaskListPayload;
    }) => updateTaskList(companyId!, id, type, payload),
    onSuccess: (_, { id }) => {
      toast.success(t('toast.task_list_updated'));
      qc.invalidateQueries({ queryKey: [QueryKeys.TaskListsList, companyId] });
      qc.invalidateQueries({
        queryKey: [QueryKeys.TaskListsDetail, companyId, id],
      });
    },
  });
}

export function useToggleTaskList() {
  const { companyId } = useAuth();
  const { t } = useTranslation(TranslationNS.TaskLists);
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      type,
      isActive,
    }: {
      id: string;
      type: TaskListType;
      isActive: boolean;
    }) => updateTaskList(companyId!, id, type, { isActive }),
    onMutate: async ({ id, isActive }) => {
      await qc.cancelQueries({
        queryKey: [QueryKeys.TaskListsList, companyId],
      });
      const previousQueries = qc.getQueriesData<GetTaskListsResponse>({
        queryKey: [QueryKeys.TaskListsList, companyId],
      });
      qc.setQueriesData<GetTaskListsResponse>(
        { queryKey: [QueryKeys.TaskListsList, companyId] },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            data: {
              ...old.data,
              data: old.data.data.map((item) =>
                item.id === id ? { ...item, isActive } : item
              ),
            },
          };
        }
      );
      return { previousQueries };
    },
    onError: (_err, _vars, context) => {
      context?.previousQueries.forEach(([queryKey, data]) => {
        qc.setQueryData(queryKey, data);
      });
      toast.error(t('toast.task_list_update_failed'));
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: [QueryKeys.TaskListsList, companyId] });
    },
  });
}

export function useDeleteTaskList() {
  const { companyId } = useAuth();
  const { t } = useTranslation(TranslationNS.TaskLists);
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, type }: { id: string; type: TaskListType }) =>
      deleteTaskList(companyId!, type, id),
    onSuccess: (_, { id }) => {
      toast.success(t('toast.task_list_deleted'));
      qc.invalidateQueries({ queryKey: [QueryKeys.TaskListsList, companyId] });
      qc.invalidateQueries({
        queryKey: [QueryKeys.TaskListsDetail, companyId, id],
      });
    },
    onError: () => {
      toast.error(t('toast.task_list_delete_failed'));
    },
  });
}

export function useGetTask(
  taskListId: string | undefined,
  type: TaskListType = 'onboarding',
  taskId: string | undefined
) {
  const { companyId } = useAuth();

  const query = useQuery({
    queryKey: [QueryKeys.TasksDetail, companyId, taskListId, taskId],
    queryFn: () => getTask(companyId!, type, taskListId!, taskId!),
    enabled: !!companyId && !!taskListId && !!taskId,
  });

  return {
    ...query,
    data: query.data?.data ?? null,
  };
}

export function useCreateTask() {
  const { companyId } = useAuth();
  const { t } = useTranslation(TranslationNS.TaskLists);
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      taskListId,
      taskListType,
      files,
      payload,
    }: {
      taskListId: string;
      taskListType: TaskListType;
      files: File[];
      payload: CreateTaskApiPayload;
    }) => {
      const newAttachments = await Promise.all(
        files.map(async (file) => {
          const presigned = await getPresignedUrl({
            filename: file.name,
            size: file.size || undefined,
            destination: `assets/companies/${companyId}/task-lists/tasks`,
            companyId,
          });
          await uploadFileToS3(file, presigned.uploadUrl);
          return {
            key: presigned.url,
            fileName: file.name,
            mimeType: file.type,
            fileSize: file.size,
          };
        })
      );
      const allAttachments = [
        ...(payload.attachments ?? []),
        ...newAttachments,
      ];
      return createTask(companyId!, taskListId, taskListType, {
        ...payload,
        attachments: allAttachments.length > 0 ? allAttachments : undefined,
      });
    },
    onSuccess: (_, { taskListId }) => {
      toast.success(t('toast.task_added'));
      qc.invalidateQueries({
        queryKey: [QueryKeys.TaskListsDetail, companyId, taskListId],
      });
      qc.invalidateQueries({ queryKey: [QueryKeys.TaskListsList, companyId] });
    },
  });
}

export function useUpdateTask() {
  const { companyId } = useAuth();
  const { t } = useTranslation(TranslationNS.TaskLists);
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      taskListId,
      taskId,
      taskListType,
      files,
      payload,
    }: {
      taskListId: string;
      taskId: string;
      taskListType: TaskListType;
      files: File[];
      payload: CreateTaskApiPayload;
    }) => {
      const newAttachments = await Promise.all(
        files.map(async (file) => {
          const presigned = await getPresignedUrl({
            filename: file.name,
            size: file.size || undefined,
            destination: `assets/companies/${companyId}/task-lists/tasks`,
            companyId,
          });
          await uploadFileToS3(file, presigned.uploadUrl);
          return {
            key: presigned.url,
            fileName: file.name,
            mimeType: file.type,
            fileSize: file.size,
          };
        })
      );
      const allAttachments = [
        ...(payload.attachments ?? []),
        ...newAttachments,
      ];
      return updateTask(companyId!, taskListId, taskId, taskListType, {
        ...payload,
        attachments: allAttachments.length > 0 ? allAttachments : undefined,
      });
    },
    onSuccess: (_, { taskListId, taskId }) => {
      toast.success(t('toast.task_updated'));
      qc.invalidateQueries({
        queryKey: [QueryKeys.TaskListsDetail, companyId, taskListId],
      });
      qc.invalidateQueries({
        queryKey: [QueryKeys.TasksDetail, companyId, taskListId, taskId],
      });
      qc.invalidateQueries({ queryKey: [QueryKeys.TaskListsList, companyId] });
    },
  });
}

export function useDeleteTask() {
  const { companyId } = useAuth();
  const { t } = useTranslation(TranslationNS.TaskLists);
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      taskListId,
      taskId,
      taskListType,
    }: {
      taskListId: string;
      taskId: string;
      taskListType: TaskListType;
    }) => deleteTask(companyId!, taskListType, taskListId, taskId),
    onSuccess: (_, { taskListId, taskId }) => {
      toast.success(t('toast.task_deleted'));
      qc.invalidateQueries({
        queryKey: [QueryKeys.TaskListsDetail, companyId, taskListId],
      });
      qc.invalidateQueries({
        queryKey: [QueryKeys.TasksDetail, companyId, taskListId, taskId],
      });
      qc.invalidateQueries({ queryKey: [QueryKeys.TaskListsList, companyId] });
    },
    onError: () => {
      toast.error(t('toast.task_delete_failed'));
    },
  });
}

export function useReorderTask(taskListId: string, type: TaskListType) {
  const { companyId } = useAuth();
  const { t } = useTranslation(TranslationNS.TaskLists);
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      taskId,
      payload,
    }: {
      taskId: string;
      payload: { orderIndex: number };
    }) => reorderTask(companyId!, type, taskListId, taskId, payload),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [QueryKeys.TaskListsDetail, companyId, taskListId],
      });
    },
    onError: () => {
      toast.error(t('toast.task_reorder_failed'));
      qc.invalidateQueries({
        queryKey: [QueryKeys.TaskListsDetail, companyId, taskListId],
      });
    },
  });
}

export function useGetParentTasks(
  taskListId: string | undefined,
  type: TaskListType = 'onboarding'
) {
  const { companyId } = useAuth();

  const query = useQuery({
    queryKey: [QueryKeys.TaskListsDetail, companyId, taskListId, 'parentOnly'],
    queryFn: () => getParentTasks(companyId!, type, taskListId!),
    enabled: !!companyId && !!taskListId,
    staleTime: 30_000,
  });

  const tasks: TaskEntity[] = query.data?.data?.tasks ?? [];
  return { ...query, tasks };
}
